import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, apiClient, useGlobalRefresh } from '../App';
import { 
  LayoutDashboard, Trophy, User, Bell, LogOut, Menu, X, 
  Settings, Users, Target, Award, Lightbulb, Zap, ChevronRight, Link2, BarChart3, MessageCircle, UserPlus, Medal, Activity, Grid3x3, CalendarCheck, Inbox
} from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { trackPageView, trackLinkClick } from '../lib/activity';
import { refreshPushSubscription } from '../lib/push';

let pushRefreshedThisSession = false;

const NavLink = ({ to, icon: Icon, children, onClick, badge }) => {
  const location = useLocation();
  const isActive = location.pathname === to;
  
  return (
    <Link 
      to={to} 
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
        isActive 
          ? 'bg-[#EFB342]/10 text-[#EFB342] border-l-2 border-[#EFB342]' 
          : 'text-stone-600 hover:text-stone-900 hover:bg-stone-100/50'
      }`}
    >
      <div className="relative">
        <Icon size={22} strokeWidth={1.5} />
        {badge && (
          <span 
            className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse"
            data-testid="unread-notification-dot"
          />
        )}
      </div>
      <span className="font-medium text-base">{children}</span>
    </Link>
  );
};

const UserNav = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/my-actions', icon: Zap, label: 'Today' },
  { to: '/leaderboard', icon: Trophy, label: 'Leaderboard' },
  { to: '/my-summaries', icon: BarChart3, label: 'Monthly Summaries' },
  { to: '/community', icon: MessageCircle, label: 'Community' },
  { to: '/profile', icon: User, label: 'Profile' },
  { to: '/notifications', icon: Bell, label: 'Notifications' },
];

const AdminNav = [
  { to: '/admin', icon: LayoutDashboard, label: 'Overview' },
  { to: '/admin/inbox', icon: Inbox, label: 'Inbox' },
  { to: '/admin/challenges', icon: Target, label: 'Challenges' },
  { to: '/admin/actions', icon: Zap, label: 'Actions' },
  { to: '/admin/milestones', icon: Award, label: 'Milestones' },
  { to: '/admin/tips', icon: Lightbulb, label: 'Daily Tips' },
  { to: '/admin/automations', icon: Settings, label: 'Automations' },
  { to: '/admin/community', icon: MessageCircle, label: 'Community' },
  { to: '/admin/group-wins', icon: Medal, label: 'Group Wins' },
  { to: '/admin/leaderboard', icon: Trophy, label: 'Leaderboard' },
  { to: '/admin/notifications', icon: Bell, label: 'Send Notifications' },
  { to: '/admin/invites', icon: Link2, label: 'Invite Links' },
  { to: '/admin/prospects', icon: UserPlus, label: 'Prospects' },
  { to: '/admin/users', icon: Users, label: 'Users' },
  { to: '/admin/activity', icon: CalendarCheck, label: 'Daily Activity' },
  { to: '/admin/summaries', icon: BarChart3, label: 'Monthly Summaries' },
  { to: '/admin/checkins-matrix', icon: Grid3x3, label: 'Daily Points Matrix' },
  { to: '/admin/system-health', icon: Activity, label: 'System Health' },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasUnreadCommunity, setHasUnreadCommunity] = useState(false);
  const [hasNewFullSendBroadcast, setHasNewFullSendBroadcast] = useState(false);
  const [adminAlertCount, setAdminAlertCount] = useState(0);
  
  const isAdmin = user?.is_admin;
  const navItems = isAdmin && window.location.pathname.startsWith('/admin') ? AdminNav : UserNav;

  useEffect(() => {
    if (!user || pushRefreshedThisSession) return;
    pushRefreshedThisSession = true;
    refreshPushSubscription();
  }, [user]);

  const fetchAdminAlerts = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await apiClient.get('/admin/alerts/unread-count');
      setAdminAlertCount(res.data.count || 0);
    } catch (err) {
      setAdminAlertCount(0);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchAdminAlerts();
    const interval = setInterval(fetchAdminAlerts, 60000);
    window.addEventListener('adminAlertsRead', fetchAdminAlerts);
    return () => {
      clearInterval(interval);
      window.removeEventListener('adminAlertsRead', fetchAdminAlerts);
    };
  }, [isAdmin, fetchAdminAlerts]);

  // Fetch unread notification count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await apiClient.get('/notifications/unread-count');
      const count = res.data.count || 0;
      setUnreadCount(count);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
      // On error, reset to 0 to avoid showing stale data
      setUnreadCount(0);
    }
  }, []);

  // Fetch unread Full Send Day broadcast for Leaderboard red dot
  const fetchFullSendBroadcast = useCallback(async () => {
    try {
      const res = await apiClient.get('/notifications/unread-count?type=full_send_broadcast');
      setHasNewFullSendBroadcast((res.data.count || 0) > 0);
    } catch (err) {
      setHasNewFullSendBroadcast(false);
    }
  }, []);

  // Fetch unread community status
  const fetchUnreadCommunity = useCallback(async () => {
    try {
      const res = await apiClient.get('/community-notes/unread/check');
      setHasUnreadCommunity(res.data.has_unread || false);
    } catch (err) {
      console.error('Failed to fetch community status:', err);
      setHasUnreadCommunity(false);
    }
  }, []);

  // Mark community as read when visiting community page. Also clear the
  // Full Send Day broadcast red dot when visiting the Leaderboard.
  useEffect(() => {
    const markCommunityRead = async () => {
      if (location.pathname === '/community' || location.pathname === '/admin/community') {
        try {
          await apiClient.post('/community-notes/mark-read');
          setHasUnreadCommunity(false);
        } catch (err) {
          console.error('Failed to mark community as read:', err);
        }
      }
    };
    const markFullSendBroadcastRead = async () => {
      if (location.pathname === '/leaderboard' && hasNewFullSendBroadcast) {
        try {
          await apiClient.put('/notifications/mark-type-read?type=full_send_broadcast');
          setHasNewFullSendBroadcast(false);
        } catch (err) {
          console.error('Failed to mark broadcast as read:', err);
        }
      }
    };
    markCommunityRead();
    markFullSendBroadcastRead();
  }, [location.pathname, hasNewFullSendBroadcast]);

  // Member activity tracking (admins are never tracked)
  useEffect(() => {
    if (!user || isAdmin || location.pathname.startsWith('/admin')) return;
    trackPageView(location.pathname);
  }, [location.pathname, user, isAdmin]);

  useEffect(() => {
    if (!user || isAdmin) return;
    const onClick = (e) => {
      const anchor = e.target.closest && e.target.closest('a[href]');
      if (anchor) trackLinkClick(anchor);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [user, isAdmin]);

  // Fetch on mount
  useEffect(() => {
    fetchUnreadCount();
    fetchUnreadCommunity();
    fetchFullSendBroadcast();
  }, [fetchUnreadCount, fetchUnreadCommunity, fetchFullSendBroadcast]);

  // Subscribe to global refresh events - this triggers on EVERY page navigation
  useGlobalRefresh(fetchUnreadCount);
  useGlobalRefresh(fetchUnreadCommunity);
  useGlobalRefresh(fetchFullSendBroadcast);

  // Listen for custom event when notifications are read/deleted
  useEffect(() => {
    const handleNotificationChange = () => {
      fetchUnreadCount();
    };
    window.addEventListener('notificationsUpdated', handleNotificationChange);
    return () => window.removeEventListener('notificationsUpdated', handleNotificationChange);
  }, [fetchUnreadCount]);

  // Listen for custom event when community notes are added
  useEffect(() => {
    const handleCommunityChange = () => {
      fetchUnreadCommunity();
    };
    window.addEventListener('communityUpdated', handleCommunityChange);
    return () => window.removeEventListener('communityUpdated', handleCommunityChange);
  }, [fetchUnreadCommunity]);

  // Poll for unread count every 2 minutes as a fallback
  useEffect(() => {
    const interval = setInterval(() => {
      fetchUnreadCount();
      fetchUnreadCommunity();
      fetchFullSendBroadcast();
    }, 120000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount, fetchUnreadCommunity, fetchFullSendBroadcast]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navBadge = (to) =>
    (to === '/notifications' && unreadCount > 0) ||
    ((to === '/community' || to === '/admin/community') && hasUnreadCommunity) ||
    (to === '/leaderboard' && hasNewFullSendBroadcast) ||
    (to === '/admin/inbox' && adminAlertCount > 0);
  const menuHasBadge = navItems.some((item) => navBadge(item.to));

  const Sidebar = ({ mobile = false, onClose }) => (
    <div className={`flex flex-col h-full ${mobile ? '' : 'w-64'}`}>
      {/* Logo */}
      <div className="p-6 border-b border-stone-200">
        <Link to={isAdmin ? '/admin' : '/dashboard'} className="flex items-center gap-2" onClick={onClose}>
          <div className="w-10 h-10 bg-[#EFB342] rounded-lg flex items-center justify-center">
            <span className="text-black font-heading font-bold text-xl">Rx</span>
          </div>
          <div>
            <h1 className="font-heading text-xl font-bold text-stone-900 tracking-wide">RxGRIT</h1>
            <p className="text-[10px] text-stone-500 uppercase tracking-widest">by BUILD Nutrition</p>
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {navItems.map(item => (
          <NavLink 
            key={item.to} 
            to={item.to} 
            icon={item.icon} 
            onClick={onClose}
            badge={navBadge(item.to)}
          >
            {item.label}
          </NavLink>
        ))}
        
        {/* Admin/User Switch */}
        {isAdmin && (
          <div className="pt-4 mt-4 border-t border-stone-200">
            {window.location.pathname.startsWith('/admin') ? (
              <NavLink to="/dashboard" icon={User} onClick={onClose}>
                User View
              </NavLink>
            ) : (
              <NavLink to="/admin" icon={Settings} onClick={onClose}>
                Admin Panel
              </NavLink>
            )}
          </div>
        )}
      </nav>

      {/* User Info & Logout */}
      <div className="p-4 border-t border-stone-200">
        <div className="flex items-center gap-3 mb-4 px-2">
          <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center">
            <User size={20} className="text-stone-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-stone-900 truncate">{user?.name}</p>
            <p className="text-xs text-stone-500 truncate">{user?.email}</p>
          </div>
        </div>
        <Button 
          variant="ghost" 
          className="w-full justify-start text-stone-600 hover:text-stone-900 hover:bg-stone-100"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut size={18} className="mr-2" />
          Sign Out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F5F4F1] flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-stone-200 bg-[#F5F4F1]">
        <Sidebar />
      </aside>

      {/* Mobile Header & Sheet */}
      <div className="flex-1 flex flex-col">
        <header className="lg:hidden sticky top-0 z-40 flex items-center justify-between px-4 py-3 border-b border-stone-200 bg-white/95 backdrop-blur-sm">
          <Link to={isAdmin ? '/admin' : '/dashboard'} className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#EFB342] rounded-lg flex items-center justify-center">
              <span className="text-black font-heading font-bold text-sm">Rx</span>
            </div>
            <span className="font-heading text-lg font-bold text-stone-900">RxGRIT</span>
          </Link>
          
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn" className="relative">
                <Menu size={24} className="text-stone-900" />
                {menuHasBadge && (
                  <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" data-testid="mobile-menu-unread-dot" />
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0 bg-[#F5F4F1] border-stone-200">
              <Sidebar mobile onClose={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto w-full max-w-full">
          <div className="w-full max-w-full overflow-x-hidden">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
